import React from "react";
import { DetailValueTypeEnumValue } from "@panda0/panda0-graphql/defs";
import {
  Checkbox,
  Input,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from "@panda0/panda0-ui";
import { Github, Linkedin, Upload, Video } from "lucide-react";
import { useFormContext } from "react-hook-form";

import { FormData } from "./types";

const FormPreview: React.FC = () => {
  const { watch } = useFormContext<FormData>();

  // Get values from the form
  const screeningQuestions = watch("screeningQuestions");
  const jobDetails = watch("jobDetails");
  const formDetails = watch("formDetails");
  const videoResume = watch("videoResume");
  const submissionSettings = watch("submissionSettings");

  // Function to render different question types
  const renderQuestionPreview = (question: any) => {
    switch (question.answerType) {
      case DetailValueTypeEnumValue.oneline:
        return (
          <Input disabled placeholder="Candidate's answer" className="mt-2" />
        );

      case DetailValueTypeEnumValue.multiline:
        return (
          <Textarea
            disabled
            placeholder="Candidate's answer"
            className="mt-2"
          />
        );

      case DetailValueTypeEnumValue.radiobutton:
        return (
          <RadioGroup className="mt-2 space-y-2">
            {(question.options || []).map((option: string, i: number) => (
              <div key={i} className="flex items-center space-x-2">
                <RadioGroupItem value={`option-${i}`} disabled />
                <label>{option || `Option ${i + 1}`}</label>
              </div>
            ))}
          </RadioGroup>
        );

      case DetailValueTypeEnumValue.checkbox:
        return (
          <div className="mt-2 space-y-2">
            {(question.options || []).map((option: string, i: number) => (
              <div key={i} className="flex items-center space-x-2">
                <Checkbox disabled />
                <label>{option || `Option ${i + 1}`}</label>
              </div>
            ))}
          </div>
        );

      default:
        return (
          <Input disabled placeholder="Candidate's answer" className="mt-2" />
        );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 max-w-lg mx-auto">
      <h2 className="text-xl font-bold mb-6">Application Preview</h2>

      {/* Job Title */}
      <div className="mb-6 p-4 border rounded-md bg-gray-50">
        <h3 className="font-medium mb-2">Job Summary</h3>
        <div className="text-sm space-y-1">
          {jobDetails?.JOBINFORMATION_SHORTDESCRIPTION && (
            <p>{jobDetails.JOBINFORMATION_SHORTDESCRIPTION}</p>
          )}
          <div className="grid grid-cols-2 gap-2 mt-2">
            {jobDetails?.JOBINFORMATION_WORKMODE && (
              <p>
                <strong>Work Mode:</strong> {jobDetails.JOBINFORMATION_WORKMODE}
              </p>
            )}
            {jobDetails?.JOBINFORMATION_WORKTYPE && (
              <p>
                <strong>Work Type:</strong> {jobDetails.JOBINFORMATION_WORKTYPE}
              </p>
            )}
            {jobDetails?.JOBINFORMATION_WORKLOCATION && (
              <p>
                <strong>Location:</strong> {jobDetails.JOBINFORMATION_WORKLOCATION}
              </p>
            )}
            {jobDetails?.JOBINFORMATION_SALARYMIN && jobDetails.JOBINFORMATION_SALARYMAX && (
              <p>
                <strong>Salary:</strong> {jobDetails.JOBINFORMATION_SALARYCURRENCY}{" "}
                {jobDetails.JOBINFORMATION_SALARYMIN} - {jobDetails.JOBINFORMATION_SALARYMAX}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Basic info fields */}
      <div className="space-y-2">
        <label className="block font-medium">
          Email Address <span className="text-red-500">*</span>
        </label>
        <Input disabled placeholder="john@example.com" />
      </div>
      <div className="mb-6 space-y-4">
        <div className="space-y-2">
          <label className="block font-medium">
            Full Name <span className="text-red-500">*</span>
          </label>
          <Input disabled placeholder="John Doe" />
        </div>

        {formDetails?.FORMSETTINGS_CONTACTNUMBEREQUIRED && (
          <div className="space-y-2">
            <label className="block font-medium">
              Contact Number{" "}
              {formDetails?.FORMSETTINGS_CONTACTNUMBEREQUIRED && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <Input disabled placeholder="+1 (555) 123-4567" />
          </div>
        )}

        {formDetails?.FORMSETTINGS_LOCATIONREQUIRED && (
          <div className="space-y-2">
            <label className="block font-medium">
              Location{" "}
              {formDetails?.FORMSETTINGS_LOCATIONREQUIRED && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <Input disabled placeholder="City, Country" />
          </div>
        )}

        {formDetails?.FORMSETTINGS_EDUCATIONREQUIRED && (
          <div className="space-y-2">
            <label className="block font-medium">
              Education{" "}
              {formDetails?.FORMSETTINGS_EDUCATIONREQUIRED && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <Input disabled placeholder="Highest qualification" />
          </div>
        )}

        {formDetails?.FORMSETTINGS_CTCREQUIRED && (
          <div className="space-y-2">
            <label className="block font-medium">
              Current CTC{" "}
              {formDetails?.FORMSETTINGS_CTCREQUIRED && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <Input disabled placeholder="Annual salary" />
          </div>
        )}

        {formDetails?.FORMSETTINGS_LINKEDINREQUIRED && (
          <div className="space-y-2">
            <label className="block font-medium">
              LinkedIn Profile{" "}
              {formDetails?.FORMSETTINGS_LINKEDINREQUIRED && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <div className="flex items-center border rounded-md p-2 bg-gray-50">
              <Linkedin className="mr-2 h-4 w-4 text-blue-600" />
              <span className="text-gray-500 text-sm">
                linkedin.com/in/username
              </span>
            </div>
          </div>
        )}

        {formDetails?.FORMSETTINGS_GITHUBREQUIRED && (
          <div className="space-y-2">
            <label className="block font-medium">
              GitHub Profile{" "}
              {formDetails?.FORMSETTINGS_GITHUBREQUIRED && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <div className="flex items-center border rounded-md p-2 bg-gray-50">
              <Github className="mr-2 h-4 w-4 text-gray-700" />
              <span className="text-gray-500 text-sm">github.com/username</span>
            </div>
          </div>
        )}
      </div>

      {/* Custom screening questions */}
      {screeningQuestions?.length > 0 && (
        <div className="mb-6">
          <h3 className="font-medium mb-4">Qualifying Questions</h3>
          <div className="space-y-6">
            {screeningQuestions.map((question, index) => (
              <div key={index} className="border p-4 rounded-md">
                <label className="block font-medium">
                  {question.SCREENING_TEXT}{" "}
                  {question.SCREENING_ISREQUIRED && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                {renderQuestionPreview(question)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resume upload */}
      {(formDetails?.FORMSETTINGS_RESUMEREQUIRED ||
        submissionSettings?.QUIZSUBMISSION_RESUME_UPLOAD) && (
          <div className="mb-6 space-y-2">
            <label className="block font-medium">
              Resume{" "}
              {formDetails?.FORMSETTINGS_RESUMEREQUIRED && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <div className="border border-dashed rounded-md p-4 text-center text-gray-500">
              <Upload className="h-6 w-6 mx-auto mb-2" />
              <p>Upload your resume (PDF, DOC, DOCX)</p>
            </div>
          </div>
        )}

      {/* Video resume */}
      {videoResume?.QUIZSUBMISSION_VIDEO_RESUME && (
        <div className="mb-6 space-y-2">
          <label className="block font-medium">Video Introduction</label>
          <div className="border border-dashed rounded-md p-4 text-center text-gray-500">
            <Video className="h-6 w-6 mx-auto mb-2" />
            <p>
              {videoResume?.QUIZSUBMISSION_VIDEO_RESUME_INSTRUCTIONS || "Record a short video introduction"}
              {videoResume?.QUIZSUBMISSION_VIDEO_RESUME_MAXDURATION && (
                <span className="block text-xs mt-1">
                  Maximum duration: {parseInt(videoResume?.QUIZSUBMISSION_VIDEO_RESUME_MAXDURATION) / 60}{" "}
                  minutes
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Candidate consent */}
      {formDetails?.FORMSETTINGS_CANDIDATECONSENTREQUIRED && (
        <div className="mb-6">
          <div className="flex items-start space-x-2">
            <Checkbox disabled />
            <label className="text-sm">
              {formDetails.FORMSETTINGS_CONSENTTEXT ||
                "I consent to the processing of my personal data in accordance with the privacy policy."}
            </label>
          </div>
        </div>
      )}

      <button className="w-full py-2 bg-primary text-white rounded-md" disabled>
        Submit Application
      </button>
    </div>
  );
};

export default React.memo(FormPreview);
